import { CallPROSolarmanFirmwareDetailsResponse } from 'ngx-modbus';

/**
 * Firmware types supported (same as app-device-solarman)
 */
export type FirmwareType = 'logger' | 'inverter' | 'battery';

/**
 * Metadata entry stored in the single Firestore document.
 * Key format: {productId}_{solarmanFirmwareId}
 */
export interface FirmwareMetadataEntry {
  // Firmware context (from Solarman API at save time)
  solarmanId: number;             // Solarman firmware ID
  version: string;                // Firmware version string
  name: string;                   // Firmware name
  productId: string;              // Solarman product ID
  firmwareType: FirmwareType;     // logger | inverter | battery
  fileSize?: number;              // File size in bytes

  // User-editable metadata
  reference: string;              // Alternative name
  tags: string[];                 // Assigned tags (suggested + custom)
  loggerType?: string;            // Logger model type (required for logger firmware)
  serialPatterns?: string[];      // Inverter serial patterns (required for inverter firmware, can match multiple models)
  notes?: string;                 // Optional notes
  releaseDate?: number;           // Release date timestamp ms
  updatedAt: number;              // Timestamp ms for tracking
}

/**
 * Single Firestore document structure containing all firmware metadata.
 * Document path: firmware-metadata/all
 */
export interface FirmwareMetadataStore {
  [firmwareKey: string]: FirmwareMetadataEntry;
}

/**
 * Firmware from Solarman API merged with Firestore metadata
 */
export interface FirmwareWithMetadata {
  // From Solarman API
  id: number;
  version: string;
  name: string;
  productId: string;
  firmwareType: FirmwareType;
  versionKey?: string;
  fileSize?: number;

  // From Firestore (optional, may not exist)
  metadata?: FirmwareMetadataEntry;
}

/**
 * Input data for saving metadata
 */
export interface FirmwareMetadataInput {
  reference: string;
  tags: string[];
  loggerType?: string;
  serialPatterns?: string[];
  notes?: string;
  releaseDate?: number;
}

/**
 * Convert Solarman firmware response to FirmwareWithMetadata
 */
export function mapSolarmanFirmware(
  fw: CallPROSolarmanFirmwareDetailsResponse,
  productId: string,
  firmwareType: FirmwareType,
  metadata?: FirmwareMetadataEntry
): FirmwareWithMetadata {
  return {
    id: fw.id,
    version: fw.version,
    name: fw.name,
    productId,
    firmwareType,
    versionKey: fw.versionKey,
    fileSize: fw.fileSize,
    metadata,
  };
}

/**
 * Generate key for firmware metadata
 */
export function getFirmwareKey(productId: string, firmwareId: number): string {
  return `${productId}_${firmwareId}`;
}
